import { validatedDisplayView } from "../../i18n/display-view.js";
import { portalText } from "../../i18n/portal.js";
/**
 * Area `do-not-call` of the console gateway — owned by plan-002 unit **RB-27** (design §15.6.7).
 *
 * The customer's own do-not-call list and the public opt-out page (P14-04's screens), over the
 * seven routes RB-27 added. The register is EMPTY: every operation is served.
 *
 * The list is per ACCOUNT. `removeDoNotCallEntry` exercises `do_not_call_entry.remove`, which an
 * impersonated session is denied (§15.5.1): operators may add but never remove (§4.10). The API
 * answers that with `403 impersonation_forbidden`, and an API key with `403 permission_denied`;
 * both come back as the generated client's `ApiError`, so the screen's error line shows the
 * refusal rather than a row that silently stays.
 *
 * NOTHING HERE BORROWS A `/v1/staff/**` ROUTE. The staff do-not-call operations
 * (`listStaffDoNotCallEntries`, `addStaffDoNotCallEntry`, `exportStaffDoNotCallEntries`) answer
 * for every account and are authorised for operators; calling one from the customer console would
 * be a cross-tenant read dressed up as a working screen. Every method below calls a customer
 * operation, and the suite beside this file asserts it.
 *
 * ── HOW A FILE GETS HERE (C16, DD-6) ─────────────────────────────────────────────────────
 * No gateway signature changed. The screen reads the chosen file with `File.text()` and passes
 * the TEXT as `uploadHandle`; this slice sends it as `source_text`. The API bounds it —
 * `413 payload_too_large` over `WETALK_API_UPLOAD_MAX_BYTE`, `422 contact_source_too_many_rows`
 * over `WETALK_API_UPLOAD_MAX_ROW` — and both surface as `ApiError`.
 *
 * ── THE MAPPING ──────────────────────────────────────────────────────────────────────────
 * Every `…Text` and label is worded here from facts (design §15.5.2): `whenText` through
 * `formatWhen(added_at)`, `scopeText` from the scope and the agent's name, each stat's label and
 * note from its code, `expiresAtText` from `expires_at` against the injected clock, a missing
 * language's name from its BCP-47 code in that language (`Intl.DisplayNames`). The refusal card
 * shows exactly what the API sends: the API lists only phrases a native speaker signed off, and a
 * language without them is in `languagesMissing` — this slice never fills a gap with another
 * language's phrases.
 *
 * `optOutPageLink` is NOT an API call: the public site's origin is configuration the console
 * already holds, and the page is served by `apps/web-public`.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type {
  ConsoleGateway,
  DoNotCallDraftView,
  DoNotCallFilterView,
  DoNotCallImportView,
  DoNotCallPageView,
  DoNotCallRemovalView,
  DoNotCallRowView,
  DoNotCallScopeView,
  DoNotCallSourceView,
  DoNotCallStatView,
  DoNotCallUnreadableProblemView,
  DoNotCallUnreadableRowView,
  ExportArtefactView,
  OptOutPageView,
  RefusalPhraseCardView,
} from "../gateway.js";
import {
  MalformedResponseError,
  readArray,
  readRecord,
  readText,
  readTextArray,
  readWholeNumber,
} from "../decode.js";
import { EVERY_VOICE_AGENT_SCOPE, optOutPageUrl } from "../suppression-view.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

declare module "../gateway.js" {
  interface ConsoleGateway {
    listDoNotCallEntries(
      filter: DoNotCallFilterView,
      search: string,
      cursor: string | null,
    ): Promise<DoNotCallPageView>;
    addDoNotCallEntry(draft: DoNotCallDraftView): Promise<DoNotCallRowView>;
    /** A customer may remove any entry, including one a WeTalk operator added. */
    removeDoNotCallEntry(doNotCallEntryId: string): Promise<void>;
    /** Several at once, with the same rules as one (customer-portal review #254). */
    removeDoNotCallEntries(doNotCallEntryId: readonly string[]): Promise<DoNotCallRemovalView>;
    importDoNotCallEntries(uploadHandle: string): Promise<DoNotCallImportView>;
    exportDoNotCallEntriesCsv(): Promise<ExportArtefactView>;
    /** The phrases the agent listens for, per language pack, with the AC-30 deadline beside them. */
    getRefusalPhrases(): Promise<RefusalPhraseCardView>;
    getOptOutPage(): Promise<OptOutPageView>;
    /**
     * The public page's URL. A string, not a fetch.
     *
     * It is composed from the public site's origin the way the Shop view's link is, because the
     * page is served by `apps/web-public` and is scoped to one business (AC-32). The console
     * shows it and opens it; it never submits to it.
     */
    optOutPageLink(businessSlug: string): string;
  }
}

export const doNotCallGap: readonly GatewayGap[] = [];

const SOURCE: readonly DoNotCallSourceView[] = [
  "said_on_call",
  "opt_out_page",
  "added_by_member",
  "added_by_operator",
];

const SCOPE: readonly DoNotCallScopeView[] = ["every_voice_agent", "one_voice_agent"];

/** Each figure's label and note, worded here from its code (design §15.5.2). */
const STAT_COPY: Readonly<Record<DoNotCallStatView["code"], { label: string; note: string }>> = validatedDisplayView({
  get on_the_list() { return validatedDisplayView({ get label() { return portalText("shell", "on_the_list_f6ba2f3e"); }, get note() { return portalText("shell", "across_all_agents_b7eec85f"); } }); },
  get added_this_week() { return validatedDisplayView({ get label() { return portalText("shell", "added_this_week_30af6ad6"); }, get note() { return portalText("shell", "in_the_last_7_days_519154f5"); } }); },
  get conversations_skipped() { return validatedDisplayView({ get label() { return portalText("shell", "calls_skipped_5b894ad3"); }, get note() { return portalText("shell", "this_month_by_campaigns_5d7eb4c2"); } }); },
  get dialled_in_error() { return validatedDisplayView({ get label() { return portalText("shell", "dialled_by_mistake_557efa1e"); }, get note() { return portalText("shell", "this_month_should_be_zero_fab042f7"); } }); },
});

const STAT_SEQUENCE: readonly DoNotCallStatView["code"][] = [
  "on_the_list",
  "added_this_week",
  "conversations_skipped",
  "dialled_in_error",
];

function readSource(value: unknown, path: string): DoNotCallSourceView {
  const raw = readText(value, path);
  const found = SOURCE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a do-not-call source", raw);
  return found;
}

function readScope(value: unknown, path: string): DoNotCallScopeView {
  const raw = readText(value, path);
  const found = SCOPE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a do-not-call scope", raw);
  return found;
}

function readNullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

const UNREADABLE_PROBLEM: readonly DoNotCallUnreadableProblemView[] = [
  "missing_number",
  "invalid_number",
  "too_many_fields",
];

/** One unreadable line of an import (review #252). */
function unreadableRowOf(wire: unknown, path: string): DoNotCallUnreadableRowView {
  const row = readRecord(wire, path);
  const raw = readText(row["problem"], `${path}.problem`);
  const problem = UNREADABLE_PROBLEM.find((one) => one === raw);
  if (problem === undefined) throw new MalformedResponseError(`${path}.problem`, "an unreadable-row reason", raw);
  return { line: readWholeNumber(row["line"], `${path}.line`), problem };
}

/** One `DoNotCallRow` as the table renders it. */
export function doNotCallRowOf(
  wire: unknown,
  path: string,
  now: Date,
  context: SliceContext,
): DoNotCallRowView {
  const row = readRecord(wire, path);
  const source = readSource(row["source"], `${path}.source`);
  const scope = readScope(row["scope"], `${path}.scope`);
  const voiceAgentName = readNullableText(row["voice_agent_name"], `${path}.voice_agent_name`);
  if (scope === "one_voice_agent" && voiceAgentName === null) {
    // The table's foreign key makes a one-agent entry name an agent that exists; a row without
    // that name would render "covers: nobody", which is a server defect, not a fact.
    throw new MalformedResponseError(`${path}.voice_agent_name`, "the agent's name", null);
  }
  const operatorName = readNullableText(
    row["added_by_operator_name"],
    `${path}.added_by_operator_name`,
  );
  const conversationId = readNullableText(row["conversation_id"], `${path}.conversation_id`);
  const conversationVoiceAgentId = readNullableText(
    row["conversation_voice_agent_id"],
    `${path}.conversation_voice_agent_id`,
  );
  if ((conversationId === null) !== (conversationVoiceAgentId === null)) {
    // Every conversation has an agent (`conversation.voice_agent_id` is NOT NULL), and no agent is
    // named without a conversation; one without the other is a server defect, not a fact.
    throw new MalformedResponseError(
      `${path}.conversation_voice_agent_id`,
      "an agent exactly when there is a conversation",
      conversationVoiceAgentId,
    );
  }
  return validatedDisplayView({
    doNotCallEntryId: readText(row["do_not_call_entry_id"], `${path}.do_not_call_entry_id`),
    e164: readText(row["e164"], `${path}.e164`),
    displayName: readNullableText(row["display_name"], `${path}.display_name`),
    spokenWords: readNullableText(row["spoken_words"], `${path}.spoken_words`),
    source,
    scope,
    get scopeText() { return scope === "every_voice_agent" ? EVERY_VOICE_AGENT_SCOPE() : portalText("shell", "value_only_0354044b", { v0: String(String(voiceAgentName)) }); },
    get whenText() { return context.formatWhen(readText(row["added_at"], `${path}.added_at`), now); },
    get addedByOperatorName() { return source === "added_by_operator" && operatorName !== null ? portalText("shell", "value_wetalk_30333e99", { v0: String(operatorName) }) : null; },
    addedByPersonId: readNullableText(row["added_by_person_id"], `${path}.added_by_person_id`),
    addedByPersonName: readNullableText(row["added_by_person_name"], `${path}.added_by_person_name`),
    get note() { return readNullableText(row["note"], `${path}.note`); },
    optOutMessage: readNullableText(row["opt_out_message"], `${path}.opt_out_message`),
    conversationId,
    conversationVoiceAgentId,
  });
}

function statOf(wire: unknown): readonly DoNotCallStatView[] {
  const stat = readRecord(wire, "listDoNotCallEntries.stat");
  return STAT_SEQUENCE.map((code) => ({
    code,
    label: STAT_COPY[code].label,
    value: readWholeNumber(stat[code], `listDoNotCallEntries.stat.${code}`),
    note: STAT_COPY[code].note,
  }));
}

/** "in 15 minutes" — the link is short-lived, so the useful fact is how long it has left. */
export function expiresInText(expiresAt: string, now: Date): string {
  const minute = Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / 60_000);
  if (Number.isNaN(minute)) throw new MalformedResponseError("expires_at", "a date-time", expiresAt);
  if (minute <= 0) return "expired";
  return minute === 1 ? portalText("shell", "in_1_minute_2298b716") : portalText("shell", "in_value_minutes_45992b3b", { v0: String(String(minute)) });
}

/**
 * A missing language's own name, from its BCP-47 code. The API sends codes because a missing
 * language may have no pack to take a name from; the runtime's `Intl` data names it in itself.
 */
export function languageNameOf(code: string): string {
  try {
    const name = new Intl.DisplayNames([code], { type: "language" }).of(code);
    return name === undefined || name === "" ? code : name;
  } catch {
    // Not a code `Intl` can read: show the code as sent rather than invent a name for it.
    return code;
  }
}

export function doNotCallApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): Pick<
  ConsoleGateway,
  | "listDoNotCallEntries"
  | "addDoNotCallEntry"
  | "removeDoNotCallEntry"
  | "removeDoNotCallEntries"
  | "importDoNotCallEntries"
  | "exportDoNotCallEntriesCsv"
  | "getRefusalPhrases"
  | "getOptOutPage"
  | "optOutPageLink"
> {
  return {
    async listDoNotCallEntries(
      filter: DoNotCallFilterView,
      search: string,
      cursor: string | null,
    ): Promise<DoNotCallPageView> {
      const now = context.now();
      const query: { source?: string; search?: string; cursor?: string } = {};
      if (filter !== "all") query.source = filter;
      if (search.trim() !== "") query.search = search.trim();
      if (cursor !== null) query.cursor = cursor;
      const body = readRecord(
        assertOk(await client.listDoNotCallEntries({ query })),
        "listDoNotCallEntries",
      );
      const next = body["next_cursor"];
      return {
        row: readArray(body["data"], "listDoNotCallEntries.data").map((one, index) =>
          doNotCallRowOf(one, `doNotCallEntry[${String(index)}]`, now, context),
        ),
        nextCursor: next === null ? null : readText(next, "listDoNotCallEntries.next_cursor"),
        matchedCount: readWholeNumber(body["matched_count"], "listDoNotCallEntries.matched_count"),
        stat: statOf(body["stat"]),
      };
    },

    async addDoNotCallEntry(draft: DoNotCallDraftView): Promise<DoNotCallRowView> {
      const body = readRecord(
        assertOk(
          await client.addDoNotCallEntry({
            body: {
              e164: draft.e164,
              display_name: draft.displayName,
              scope: draft.scope,
              voice_agent_id: draft.scope === "one_voice_agent" ? draft.voiceAgentId : null,
              note: draft.note,
            },
          }),
        ),
        "addDoNotCallEntry",
      );
      return doNotCallRowOf(body["data"], "addDoNotCallEntry.data", context.now(), context);
    },

    async removeDoNotCallEntry(doNotCallEntryId: string): Promise<void> {
      assertOk(
        await client.removeDoNotCallEntry({ path: { do_not_call_entry_id: doNotCallEntryId } }),
      );
    },

    async removeDoNotCallEntries(doNotCallEntryId: readonly string[]): Promise<DoNotCallRemovalView> {
      const data = readRecord(
        readRecord(
          assertOk(await client.removeDoNotCallEntries({ body: { do_not_call_entry_id: doNotCallEntryId } })),
          "removeDoNotCallEntries",
        )["data"],
        "removeDoNotCallEntries.data",
      );
      return {
        removedCount: readWholeNumber(data["removed_count"], "removeDoNotCallEntries.removed_count"),
        notFoundCount: readWholeNumber(data["not_found_count"], "removeDoNotCallEntries.not_found_count"),
      };
    },

    async importDoNotCallEntries(uploadHandle: string): Promise<DoNotCallImportView> {
      const data = readRecord(
        readRecord(
          assertOk(await client.importDoNotCallEntries({ body: { source_text: uploadHandle } })),
          "importDoNotCallEntries",
        )["data"],
        "importDoNotCallEntries.data",
      );
      return {
        addedCount: readWholeNumber(data["added_count"], "importDoNotCallEntries.added_count"),
        alreadyPresentCount: readWholeNumber(
          data["already_present_count"],
          "importDoNotCallEntries.already_present_count",
        ),
        unreadableCount: readWholeNumber(
          data["unreadable_count"],
          "importDoNotCallEntries.unreadable_count",
        ),
        unreadableRow: readArray(data["unreadable_row"], "importDoNotCallEntries.unreadable_row").map(
          (one, index) => unreadableRowOf(one, `importDoNotCallEntries.unreadable_row[${String(index)}]`),
        ),
      };
    },

    async exportDoNotCallEntriesCsv(): Promise<ExportArtefactView> {
      const data = readRecord(
        readRecord(assertOk(await client.exportDoNotCallEntries({})), "exportDoNotCallEntries")[
          "data"
        ],
        "exportDoNotCallEntries.data",
      );
      return validatedDisplayView({
        downloadUrl: readText(data["download_url"], "exportDoNotCallEntries.download_url"),
        fileExtension: `.${readText(data["file_extension"], "exportDoNotCallEntries.file_extension")}`,
        get expiresAtText() { return expiresInText(
          readText(data["expires_at"], "exportDoNotCallEntries.expires_at"),
          context.now(),
        ); },
        rowCount: readWholeNumber(data["row_count"], "exportDoNotCallEntries.row_count"),
      });
    },

    async getRefusalPhrases(): Promise<RefusalPhraseCardView> {
      const data = readRecord(
        readRecord(assertOk(await client.listRefusalPhrases()), "listRefusalPhrases")["data"],
        "listRefusalPhrases.data",
      );
      return {
        languageSet: readArray(data["language_set"], "listRefusalPhrases.language_set").map(
          (one, index) => {
            const path = `listRefusalPhrases.language_set[${String(index)}]`;
            const set = readRecord(one, path);
            return {
              languageCode: readText(set["language_code"], `${path}.language_code`),
              languageName: readText(set["language_name"], `${path}.language_name`),
              phrase: readTextArray(set["phrase"], `${path}.phrase`),
            };
          },
        ),
        deadlineMs: readWholeNumber(data["deadline_ms"], "listRefusalPhrases.deadline_ms"),
        languagesCoveredCount: readWholeNumber(
          data["languages_covered_count"],
          "listRefusalPhrases.languages_covered_count",
        ),
        languagesMissing: readTextArray(
          data["languages_missing"],
          "listRefusalPhrases.languages_missing",
        ).map(languageNameOf),
      };
    },

    async getOptOutPage(): Promise<OptOutPageView> {
      const data = readRecord(
        readRecord(assertOk(await client.getOptOutPage()), "getOptOutPage")["data"],
        "getOptOutPage.data",
      );
      const businessSlug = readText(data["business_slug"], "getOptOutPage.business_slug");
      return {
        businessSlug,
        displayName: readText(data["display_name"], "getOptOutPage.display_name"),
        publicUrl: optOutPageUrl(context.publicSiteBaseUrl, businessSlug),
      };
    },

    optOutPageLink: (businessSlug: string): string =>
      optOutPageUrl(context.publicSiteBaseUrl, businessSlug),
  };
}
